import * as Sentry from "@sentry/react";

/**
 * Sentry initialisation for the admin SPA (OPS-2026-002, ADR-0011).
 *
 * Called once from `main.tsx` before render. The whole integration is a
 * **no-op when `VITE_SENTRY_DSN` is unset**, which is the default for local
 * development.
 *
 * ## Privacy (BR-063, security.md)
 * Admin screens display employee GPS traces, home locations (sensitive PII),
 * emails and phones, and requests carry Clerk bearer tokens. `beforeSend`
 * strips request payloads and redacts sensitive keys before anything leaves
 * the browser. Session Replay is deliberately NOT enabled — it would record
 * those screens wholesale.
 */

const SENSITIVE_KEY_PATTERNS = [
  "latitude",
  "longitude",
  "password",
  "token",
  "secret",
  "authorization",
  "cookie",
  "apikey",
  "email",
  "phone",
];

const REDACTED = "[redacted]";

function isSensitiveKey(key: string): boolean {
  const k = key.toLowerCase();
  return SENSITIVE_KEY_PATTERNS.some((p) => k.includes(p));
}

function redact(value: unknown, depth = 0): unknown {
  if (depth > 6 || value == null) return value;
  if (Array.isArray(value)) return value.map((v) => redact(v, depth + 1));
  if (typeof value !== "object") return value;

  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
    out[k] = isSensitiveKey(k) ? REDACTED : redact(v, depth + 1);
  }
  return out;
}

function scrub<T extends Sentry.ErrorEvent | Sentry.Event>(event: T): T {
  if (event.request) {
    delete event.request.data;
    delete event.request.cookies;
    delete event.request.query_string;
    if (event.request.headers) {
      for (const key of Object.keys(event.request.headers)) {
        if (isSensitiveKey(key)) event.request.headers[key] = REDACTED;
      }
    }
  }
  // Internal user id only — never email/username/IP.
  if (event.user) event.user = { id: event.user.id };
  if (event.extra) event.extra = redact(event.extra) as typeof event.extra;
  if (event.contexts) event.contexts = redact(event.contexts) as typeof event.contexts;
  return event;
}

export function initSentry(): void {
  const dsn = import.meta.env.VITE_SENTRY_DSN;
  if (!dsn) return;

  Sentry.init({
    dsn,
    environment: import.meta.env.VITE_SENTRY_ENVIRONMENT ?? import.meta.env.MODE,
    // Injected by CI as the deployed git SHA — must match the release the
    // source maps were uploaded under, or stack traces stay minified.
    release: import.meta.env.VITE_SENTRY_RELEASE,
    integrations: [
      Sentry.browserTracingIntegration(),
      // NOTE: replayIntegration() intentionally omitted — see file header.
    ],
    tracesSampleRate: Number(import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE ?? 0.1),
    // Connect frontend traces to backend ones for the same request.
    tracePropagationTargets: [
      /^\//,
      ...(import.meta.env.VITE_API_URL ? [String(import.meta.env.VITE_API_URL)] : []),
    ],
    sendDefaultPii: false,
    beforeSend: (event) => scrub(event),
    beforeSendTransaction: (event) => scrub(event),
  });
}

/** Re-exported so `main.tsx` doesn't need its own Sentry import. */
export { Sentry };
